// Opaque payment references for credit purchases and membership payments.
// Built on the client; stored verbatim as lastCreditReference / lastRenewalReference.
//
// Formats:
//   Credit_<firstname-lastname>_<dd-mm-yyyy-hh:mm:ss>
//   Renew_<firstname-lastname>_<dd-mm-yyyy-hh:mm:ss>
//   SignUp_<firstname-lastname>_<dd-mm-yyyy-hh:mm:ss>
//
// Seconds in the timestamp avoid duplicate references within the same minute.
// Name is trimmed from the end when the full reference would exceed 60 characters
// (Zapper Hosted merchantOrderId limit).

const MAX_REFERENCE_LENGTH = 60;

const REFERENCE_PREFIXES = {
  credit: 'Credit_',
  renewal: 'Renew_',
  signup: 'SignUp_'
};

const buildNameSlug = (firstName, lastName) =>
  `${(firstName || '').trim()}-${(lastName || '').trim()}`;

// Local browser time, en-GB: dd-mm-yyyy-hh:mm:ss
const formatReferenceTimestamp = () => {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-GB').replace(/\//g, '-');
  const timeStr = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  return `${dateStr}-${timeStr}`;
};

const buildOpaqueReference = (prefix, firstName, lastName) => {
  const timestamp = formatReferenceTimestamp();
  let name = buildNameSlug(firstName, lastName);
  const overhead = prefix.length + 1 + timestamp.length;

  if (overhead >= MAX_REFERENCE_LENGTH) {
    return `${prefix}_${timestamp}`.slice(0, MAX_REFERENCE_LENGTH);
  }

  const maxNameLength = MAX_REFERENCE_LENGTH - overhead;
  if (name.length > maxNameLength) {
    name = name.slice(0, maxNameLength).replace(/[-_]+$/, '');
  }

  return `${prefix}${name}_${timestamp}`;
};

export const buildCreditReference = (firstName, lastName) =>
  buildOpaqueReference(REFERENCE_PREFIXES.credit, firstName, lastName);

export const buildRenewReference = (firstName, lastName) =>
  buildOpaqueReference(REFERENCE_PREFIXES.renewal, firstName, lastName);

export const buildSignUpReference = (firstName, lastName) =>
  buildOpaqueReference(REFERENCE_PREFIXES.signup, firstName, lastName);
